import { splitOrderNoSearch } from './orderNoSearch';

/**
 * QA-611 주문 검색어 유형 — 통합 입력창을 [유형 select + 입력창]으로 나눴다. 주문자명은 별도 칸이라 유형에서 뺐다.
 * 주문번호만 완전일치·다중 입력(쉼표·줄바꿈 최대 10개), 나머지는 부분일치·단일 입력.
 */
export type OrderSearchType = 'ORDER_NO' | 'PRODUCT_NAME' | 'PRODUCT_CODE' | 'GOODS_CD';

export const ORDER_SEARCH_TYPES: ReadonlyArray<OrderSearchType> = ['ORDER_NO', 'PRODUCT_NAME', 'PRODUCT_CODE', 'GOODS_CD'];

export const DEFAULT_ORDER_SEARCH_TYPE: OrderSearchType = 'ORDER_NO';

/** 검색어를 BE 쿼리로 — 목록·합계·엑셀이 같은 조건을 쓰도록 한곳에서 만든다. */
export interface OrderKeywordQuery {
  orderNos?: string[];
  searchType?: Exclude<OrderSearchType, 'ORDER_NO'>;
  keyword?: string;
  q?: string;
}

/**
 * - 주문번호: 한 개든 여러 개든 orderNos(완전일치, 최대 10개).
 * - 그 외: searchType + keyword(BE 유형별 부분일치). BE 반영 전에도 찾히도록 기존 통합 검색어 q 도 함께 보낸다
 *   (BE 는 searchType 이 있으면 q 를 쓰지 않는다 — docs/handoff/BO-TO-BE-2026-10-29-order-search-type.md).
 */
export const orderKeywordQuery = (
  searchType: OrderSearchType | null | undefined,
  raw: string | null | undefined,
): OrderKeywordQuery => {
  const keyword = (raw ?? '').trim();
  if (keyword === '') return {};
  if (!searchType || searchType === 'ORDER_NO') {
    if (!/[,\r\n]/.test(keyword)) return { orderNos: [keyword] };
    const { orderNos } = splitOrderNoSearch(keyword);
    return orderNos ? { orderNos } : {};
  }
  return { searchType, keyword, q: keyword };
};

/**
 * [상품주문번호별] 보기 — 자체상품코드로 찾으면 주문 안에서 일치하는 상품 줄만 보인다(설계서 p.2).
 * 목록 lines[].selfProductCode 를 부분일치(대소문자 무시)로 비교한다. 다른 유형은 주문 단위 그대로.
 */
export const selfCodeLineMatcher = (
  searchType: OrderSearchType,
  raw: string,
): ((line: { selfProductCode?: string | null }) => boolean) | undefined => {
  const keyword = raw.trim().toLowerCase();
  if (searchType !== 'GOODS_CD' || keyword === '') return undefined;
  return (line) => (line.selfProductCode ?? '').toLowerCase().includes(keyword);
};
